import { useState, useEffect, useRef } from "react";
import { mealAccents } from "../../constants/mealAccents";


function MealCard({ label, name, accent, isCooked, appTheme }) {
  return (
    <div style={{
      padding: "10px 8px 12px 10px",
      borderRadius: "10px",
      background: appTheme.softBackground,
      border: appTheme.cardBorder,
      borderLeft: `3px solid ${isCooked ? "#22c55e" : accent.circleColor}`,
      opacity: isCooked ? 0.55 : 1,
      minHeight: "70px",
      display: "flex",
      flexDirection: "column",
    }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: "5px" }}>
        <span style={{ fontSize: "9px", fontWeight: "700", color: isCooked ? "#22c55e" : accent.circleColor, textTransform: "capitalize", letterSpacing: "0.03em" }}>
          {label === "snacks" ? "Snacks" : label.charAt(0).toUpperCase() + label.slice(1)}
        </span>
        {isCooked && <span style={{ marginLeft: "auto", fontSize: "9px", color: "#22c55e", fontWeight: "700" }}>✓</span>}
      </div>
      {name ? (
        <div style={{ fontSize: "10px", fontWeight: "600", color: appTheme.primaryText, lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden", wordBreak: "break-word" }}>
          {name}
        </div>
      ) : (
        <div style={{ fontSize: "11px", color: appTheme.mutedText, fontStyle: "italic" }}>—</div>
      )}
    </div>
  );
}

function DayRow({ dayName, dayNum, monthName, isToday, hasMeals, emptyLabel, accentColor, appTheme, onEmptyClick, children }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "6px",
      padding: "10px 4px",
      borderBottom: `1px solid ${appTheme.cardBorder.replace("1px solid ", "")}`,
    }}>
      {/* Day label */}
      <div style={{ flexShrink: 0, width: "18px", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ fontSize: "8px", fontWeight: "700", color: appTheme.secondaryText, letterSpacing: "0.06em" }}>{dayName}</div>
        <div style={{ fontSize: "20px", fontWeight: "800", color: isToday ? accentColor : appTheme.primaryText, lineHeight: 1.1 }}>{dayNum}</div>
        <div style={{ fontSize: "8px", fontWeight: "600", color: appTheme.secondaryText }}>{monthName}</div>
        {isToday && <div style={{ fontSize: "8px", fontWeight: "800", color: accentColor, letterSpacing: "0.06em", marginTop: "3px", textTransform: "uppercase" }}>TODAY</div>}
      </div>

      {hasMeals ? (
        <div style={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "3px" }}>
          {children}
        </div>
      ) : onEmptyClick ? (
        <button onClick={onEmptyClick} style={{ display: "flex", alignItems: "center", justifyContent: "center", border: `1px dashed ${appTheme.accentColor}`, borderRadius: "10px", padding: "12px 16px", minHeight: "56px", flex: 1, color: appTheme.accentColor, fontSize: "13px", fontWeight: "600", background: "transparent", cursor: "pointer" }}>
          {emptyLabel}
        </button>
      ) : (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", border: `1px dashed ${appTheme.accentColor}`, borderRadius: "10px", padding: "12px 16px", minHeight: "56px", flex: 1, color: appTheme.accentColor, fontSize: "13px", fontWeight: "600" }}>
          {emptyLabel}
        </div>
      )}
    </div>
  );
}

function FullWeekView({
  onClose, onSelectDay, appTheme, isMobile, planningMode, plan, getDateKey,
  getPlanCalendarDays, planCalendarWeekOffset, currentProgrammeWeek,
  currentProgrammeWeekIndex, days, completedMeals, selectedProgramme,
  goToProgrammeWeek, getProgrammeWeekCount
}) {
  const [localOffset, setLocalOffset] = useState(planCalendarWeekOffset);
  const weekDays = getPlanCalendarDays(localOffset);
  const todayDateKey = getDateKey(new Date());
  const ACCENT = appTheme.accentColor;
  const weekStripRef = useRef(null);
  const activeWeekRef = useRef(null);

  useEffect(() => {
    if (!weekStripRef.current || !activeWeekRef.current) return;
    const container = weekStripRef.current;
    const btn = activeWeekRef.current;
    const containerCenter = container.offsetWidth / 2;
    const btnCenter = btn.offsetLeft + btn.offsetWidth / 2;
    container.scrollTo({ left: btnCenter - containerCenter, behavior: "smooth" });
  }, [currentProgrammeWeekIndex]);


  const weekCount = getProgrammeWeekCount ? getProgrammeWeekCount(selectedProgramme) : 1;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1500, background: appTheme.pageBackground, display: "flex", flexDirection: "column", overflowY: "hidden" }}>

      {/* Header */}
      <div style={{ display: "grid", gridTemplateColumns: "44px 1fr 44px", alignItems: "center", padding: isMobile ? "16px 16px 12px" : "20px 24px 14px", borderBottom: appTheme.cardBorder, background: appTheme.pageBackground, flexShrink: 0 }}>
        <button
          onClick={onClose}
          style={{ width: "40px", height: "40px", borderRadius: "999px", border: "none", background: "transparent", color: appTheme.primaryText, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "24px" }}
        >‹</button>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "18px", fontWeight: "500", fontFamily: appTheme.fontHeading, color: appTheme.primaryText }}>Week Overview</div>
          <div style={{ fontSize: "13px", color: appTheme.secondaryText, marginTop: "2px", textTransform: "capitalize" }}>{planningMode} Plan</div>
        </div>
        <div />
      </div>

      {/* Week navigation strip */}
      {(planningMode === "manual" || currentProgrammeWeek) && (
        <div style={{ padding: isMobile ? "10px 14px 8px" : "12px 20px 8px", borderBottom: appTheme.cardBorder, background: appTheme.pageBackground, flexShrink: 0 }}>
          {planningMode === "manual" ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
              <button onClick={() => setLocalOffset(p => p - 1)} style={{ width: "32px", height: "32px", borderRadius: "999px", border: appTheme.pillBorder, background: appTheme.pillBackground, color: appTheme.primaryText, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="7" height="12" viewBox="0 0 7 12" fill="none"><path d="M6 1L1 6l5 5" stroke={appTheme.primaryText} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1, justifyContent: "center" }}>
                <div style={{ fontSize: "14px", fontWeight: "700", color: appTheme.primaryText, textAlign: "center" }}>
                  {(() => {
                    const first = weekDays[0], last = weekDays[weekDays.length - 1];
                    const fmt = (d) => d.toLocaleDateString("en-GB", { day: "numeric", month: "long" });
                    return `${fmt(first)} – ${fmt(last)}`;
                  })()}
                </div>
                {localOffset !== 0 && (
                  <button
                    onClick={() => setLocalOffset(0)}
                    title="Back to current week"
                    style={{ background: "none", border: "none", cursor: "pointer", padding: "2px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                  >
                    {(() => {
                      const today = new Date();
                      const daysToMonday = (today.getDay() + 6) % 7;
                      const monday = new Date(today);
                      monday.setDate(today.getDate() - daysToMonday);
                      const mondayDate = monday.getDate();
                      return (
                        <svg viewBox="0 0 225 244" width="17" height="18" fill="none">
                          <path d="M206.25 18.75H178.125V9.375C178.125 6.8886 177.137 4.50403 175.379 2.74587C173.621 0.98772 171.236 0 168.75 0C166.264 0 163.879 0.98772 162.121 2.74587C160.363 4.50403 159.375 6.8886 159.375 9.375V18.75H65.625V9.375C65.625 6.8886 64.6373 4.50403 62.8791 2.74587C61.121 0.98772 58.7364 0 56.25 0C53.7636 0 51.379 0.98772 49.6209 2.74587C47.8627 4.50403 46.875 6.8886 46.875 9.375V18.75H18.75C13.7772 18.75 9.00805 20.7254 5.49175 24.2417C1.97544 27.7581 0 32.5272 0 37.5V225C0 229.973 1.97544 234.742 5.49175 238.258C9.00805 241.775 13.7772 243.75 18.75 243.75H206.25C211.223 243.75 215.992 241.775 219.508 238.258C223.025 234.742 225 229.973 225 225V37.5C225 32.5272 223.025 27.7581 219.508 24.2417C215.992 20.7254 211.223 18.75 206.25 18.75ZM46.875 37.5V46.875C46.875 49.3614 47.8627 51.746 49.6209 53.5041C51.379 55.2623 53.7636 56.25 56.25 56.25C58.7364 56.25 61.121 55.2623 62.8791 53.5041C64.6373 51.746 65.625 49.3614 65.625 46.875V37.5H159.375V46.875C159.375 49.3614 160.363 51.746 162.121 53.5041C163.879 55.2623 166.264 56.25 168.75 56.25C171.236 56.25 173.621 55.2623 175.379 53.5041C177.137 51.746 178.125 49.3614 178.125 46.875V37.5H206.25V75H18.75V37.5H46.875Z" fill={ACCENT}/>
                          <text x="112" y="168" textAnchor="middle" dominantBaseline="middle" fill="#ffffff" fontSize="90" fontWeight="800" fontFamily="system-ui, -apple-system, sans-serif">
                            {mondayDate}
                          </text>
                        </svg>
                      );
                    })()}
                  </button>
                )}
              </div>
              <button onClick={() => setLocalOffset(p => p + 1)} style={{ width: "32px", height: "32px", borderRadius: "999px", border: appTheme.pillBorder, background: appTheme.pillBackground, color: appTheme.primaryText, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="7" height="12" viewBox="0 0 7 12" fill="none"><path d="M1 1l5 5-5 5" stroke={appTheme.primaryText} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </div>
          ) : (
            <div ref={weekStripRef} className="hide-scrollbar" style={{ display: "flex", gap: "8px", overflowX: "auto", paddingBottom: "2px" }}>
              {Array.from({ length: weekCount }, (_, i) => {
                const isActive = currentProgrammeWeekIndex === i;
                return (
                  <button
                    key={i}
                    ref={isActive ? activeWeekRef : null}
                    onClick={() => goToProgrammeWeek && goToProgrammeWeek(i)}
                    style={{ flexShrink: 0, padding: "7px 16px", borderRadius: "999px", border: isActive ? appTheme.accentBorder : appTheme.pillBorder, background: isActive ? appTheme.accentLight : appTheme.pillBackground, color: isActive ? ACCENT : appTheme.secondaryText, fontSize: "13px", fontWeight: isActive ? "700" : "500", cursor: "pointer" }}
                  >
                    Week {i + 1}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Day rows */}
      <div className="modal-scroll-hidden" style={{ flex: 1, overflowY: "auto", padding: isMobile ? "0 8px 100px" : "0 12px 40px" }}>

        {planningMode === "manual" ? (
          weekDays.map((date) => {
            const dateKey = getDateKey(date);
            const isToday = dateKey === todayDateKey;
            const dayPlan = plan[dateKey] || {};
            const hasMeals = ["breakfast", "lunch", "dinner"].some(mt => dayPlan[mt]);
            const snacks = dayPlan.snacks || [];
            const dayName = date.toLocaleDateString("en-GB", { weekday: "short" }).toUpperCase();
            const dayNum = date.getDate();
            const monthName = date.toLocaleDateString("en-GB", { month: "short" });

            return (
              <DayRow key={dateKey} dayName={dayName} dayNum={dayNum} monthName={monthName} isToday={isToday} hasMeals={hasMeals} emptyLabel="+ Add meals" accentColor={ACCENT} appTheme={appTheme} onEmptyClick={onSelectDay ? () => onSelectDay(dateKey) : undefined}>
                {["breakfast", "lunch", "dinner"].map(mealType => (
                  <MealCard key={mealType} label={mealType} name={dayPlan[mealType]?.name} accent={mealAccents[mealType]} isCooked={!!completedMeals[`${dateKey}-${mealType}`]} appTheme={appTheme} />
                ))}
                <MealCard label="snacks" name={snacks.length > 0 ? (snacks[0].name + (snacks.length > 1 ? ` +${snacks.length - 1}` : "")) : null} accent={mealAccents.snacks} isCooked={false} appTheme={appTheme} />
              </DayRow>
            );
          })

        ) : !currentProgrammeWeek ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 24px", textAlign: "center", gap: "12px" }}>
            <div style={{ color: ACCENT }}>
              <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="5" width="18" height="16" rx="3" /><path d="M8 3v4M16 3v4M3 10h18" />
              </svg>
            </div>
            <div style={{ fontSize: "18px", fontWeight: "500", fontFamily: appTheme.fontHeading, color: appTheme.primaryText }}>No programme selected</div>
            <div style={{ fontSize: "14px", color: appTheme.secondaryText, lineHeight: 1.6, maxWidth: "240px" }}>Choose a programme to see your week overview here.</div>
          </div>

        ) : (
          days.map((day) => {
            const dayMeals = currentProgrammeWeek?.days?.[day];
            const isToday = day === days[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];
            const hasMeals = dayMeals && ["breakfast", "lunch", "dinner"].some(mt => dayMeals[mt]);

            return (
              <DayRow key={day} dayName={day.substring(0, 3).toUpperCase()} dayNum="" monthName="" isToday={isToday} hasMeals={!!hasMeals} emptyLabel="No meals" accentColor={ACCENT} appTheme={appTheme}>
                {["breakfast", "lunch", "dinner"].map(mealType => (
                  <MealCard key={mealType} label={mealType} name={dayMeals?.[mealType] || null} accent={mealAccents[mealType]} isCooked={!!completedMeals[`week${currentProgrammeWeekIndex}-${day}-${mealType}`]} appTheme={appTheme} />
                ))}
                <MealCard label="snacks" name={null} accent={mealAccents.snacks} isCooked={false} appTheme={appTheme} />
              </DayRow>
            );
          })
        )}
      </div>
    </div>
  );
}

export default FullWeekView;
